/**
 * Terms of Service content (Issue #2212, extracted from TermsPage.tsx / #387)
 *
 * Single source for web TermsPage and the native Terms screen.
 * Legal text — content changes here are policy changes; keep the
 * "Last Updated" stamp in sync with any wording edit.
 *
 * The two Privacy Policy links use `webRender: 'button'` — the web page
 * renders them as navigate() buttons, pinned by TermsPage.test.tsx.
 */

import type { ContentDoc } from '../../types/content';

export const TERMS_DOC: ContentDoc = {
  slug: 'terms',
  title: ['Terms of Service'],
  subtitle: 'The rules of the road',
  lastUpdated: 'September 2026',
  lastUpdatedPosition: 'top',
  callout: {
    title: 'TL;DR',
    blocks: [
      {
        type: 'p',
        inlines: [
          'Welcome to ',
          { t: 'brand' },
          '. By using this thing, you agree to play nice, not break stuff, and not blame us for the questionable decisions you make while running, biking, or otherwise moving your body. We provide tools; you provide common sense. Deal? Great.',
        ],
      },
    ],
  },
  groups: [
    [
      {
        showDivider: true,
        blocks: [
          {
            type: 'p',
            inlines: [
              'Welcome to ORO move. These Terms of Service ("Terms") govern your access to and use of the ORO move platform, websites, services, applications, and related features (collectively, the "Service").',
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['1. Acceptance of These Terms'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'By creating an account, accessing the Service, or using any part of ORO move, you confirm that you have read, understood, and agree to be bound by these Terms.',
            ],
          },
          {
            type: 'p',
            inlines: [
              "If you do not agree, do not use the Service. (We'll miss you, but boundaries are healthy.)",
            ],
          },
        ],
      },
      {
        title: ['2. Who We Are'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'ORO move is operated by ',
              { t: 'strong', v: 'ORO, LLC' },
              ', a company in the United States. Throughout these Terms, "we" and "us" mean ORO, LLC.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'We provide tools that help clubs, communities, race organizers, and individuals connect, coordinate, and move together. We are a platform, not a coach, not a medical expert, and definitely not responsible for your shoelaces.',
            ],
          },
        ],
      },
      {
        title: ['3. Eligibility'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'You must be at least ',
              { t: 'strong', v: '13 years old' },
              ' to use the Service. Users under 13 are not permitted to create accounts or use ORO move. If we learn that a user is under 13, we will remove their account.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'Clubs and organizations must ensure their administrators are authorized to act for them.',
            ],
          },
        ],
      },
      {
        title: ['4. Your Account'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'ORO move uses passwordless authentication: a magic link sent to your email, or a one-time code sent by text. You are responsible for:',
            ],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['Maintaining access to the email address or phone number used to sign in'],
              ["Keeping that email account or phone secure (it's your key to access)"],
              ['Ensuring any information you provide is accurate'],
              ['All activity that occurs through your account'],
            ],
          },
          {
            type: 'p',
            inlines: [
              'While we use reasonable security measures, no system is perfectly secure. If you believe your account has been compromised or notice unauthorized activity, notify us immediately. We take security seriously.',
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['5. Acceptable Use'],
        headingLevel: 'h3',
        blocks: [
          { type: 'p', inlines: ['You agree not to:'] },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['Use the Service for unlawful or harmful purposes'],
              ['Harass, threaten, or impersonate others'],
              ['Upload anything malicious, illegal, or wildly inappropriate'],
              ['Interfere with or disrupt the Service'],
              ["Attempt to access restricted areas or data that isn't yours"],
            ],
          },
          {
            type: 'p',
            inlines: ["Basically: be a good human. Don't make us write a longer list."],
          },
          {
            type: 'box',
            tone: 'surface',
            title: 'No tolerance for objectionable content:',
            blocks: [
              {
                type: 'p',
                inlines: [
                  'ORO move has no tolerance for objectionable content or abusive users. Objectionable content, including anything harassing, hateful, threatening, or otherwise abusive, is removed, and the people who post it are removed from the Service.',
                ],
              },
              {
                type: 'p',
                inlines: [
                  'You can report objectionable content from within the app, and you can block a user so their content no longer reaches you. We act on every report within 24 hours: content we confirm is objectionable is removed, and the user who posted it is ejected from the Service.',
                ],
              },
            ],
          },
        ],
      },
      {
        title: ['6. User Content'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'You may upload or submit photos, messages, event information, and other content ("User Content").',
            ],
          },
          {
            type: 'p',
            inlines: [
              'To operate and improve the Service, you grant ORO move a non-exclusive, worldwide, royalty-free license to store, host, display, format, resize, cache, and distribute your User Content solely in connection with providing the Service.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'You remain the owner of your content. For your profile and the photos you upload to it, the license ends when you delete them or close your account.',
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: 'Events you created do not go with you.' },
              ' If you close your account, any event you organized transfers to the club owner rather than disappearing, so the people who RSVP\'d still have their event. The license over it continues for as long as the club keeps it.',
            ],
          },
          {
            type: 'p',
            inlines: [
              { t: 'strong', v: 'Chat messages are the other exception, and you should know it up front.' },
              ' Delete a message and it disappears from the room, but we keep the text, and any photo attached to it, so we can investigate a report about that conversation later. Close your account and the messages you did not delete stay where they are, still visible to the club, relabelled ',
              { t: 'mono', v: '[Deleted User]' },
              '. We leave those in place because tearing holes in a shared thread is worse for everyone else in it. Our ',
              // Deliberately a plain inline link, NOT webRender: 'button' — the
              // §7 and §8 buttons are deliberate CTAs and TermsPage.test.tsx
              // pins their count at exactly two.
              { t: 'link', v: 'Privacy Policy', href: '/privacy' },
              ' describes the same behavior.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'We reserve the right to remove content that violates these Terms or applicable laws.',
            ],
          },
          {
            type: 'box',
            tone: 'surface',
            title: 'Public Content:',
            blocks: [
              {
                type: 'p',
                inlines: [
                  'If you choose to make content public (like public event descriptions or public club profiles), it may be visible to others and shared outside the Service. You are responsible for what you choose to make public.',
                ],
              },
            ],
          },
        ],
      },
      {
        title: ['7. Privacy'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'Your privacy matters. Our ',
              { t: 'link', v: 'Privacy Policy', href: '/privacy', webRender: 'button' },
              ' explains how we collect, use, and safeguard your information. By using the Service, you consent to our data practices as described there.',
            ],
          },
        ],
      },
      {
        title: ['8. SMS Communications'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'If you sign in by text, opt into SMS notifications, or have your number entered by a club member inviting you, you may receive text messages from ORO move including:',
            ],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              [
                { t: 'strong', v: 'Event notifications' },
                ' — reminders, updates, and changes',
              ],
              [
                { t: 'strong', v: 'Club updates' },
                " — announcements from clubs you've joined",
              ],
              [
                { t: 'strong', v: 'Club invitations' },
                ' — an invite when a member asks you to join their club',
              ],
              [
                { t: 'strong', v: 'Account notifications' },
                ' — one-time codes to sign in, and to confirm a phone number you add to your account',
              ],
            ],
          },
          {
            type: 'box',
            tone: 'surface',
            title: 'The fine print:',
            blocks: [
              {
                type: 'list',
                style: 'bullet',
                items: [
                  ['Message frequency may vary based on your activity and preferences'],
                  ['Message and data rates may apply depending on your carrier'],
                  [
                    'Text ',
                    { t: 'strong', v: 'STOP', tone: 'inherit' },
                    ' at any time to opt out of SMS messages',
                  ],
                  [
                    'For help, visit ',
                    { t: 'link', v: 'oro.fit/help/sms', href: '/help/sms' },
                  ],
                ],
              },
            ],
          },
          {
            type: 'p',
            inlines: [
              'For details on how we handle your phone number, see our ',
              { t: 'link', v: 'Privacy Policy', href: '/privacy', webRender: 'button' },
              '.',
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['9. Clubs, Events & Interactions'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'ORO move merely facilitates interactions between users, clubs, and organizers. We do not control, endorse, or guarantee:',
            ],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['The accuracy of event information'],
              ['The safety or quality of any activity'],
              ['The qualifications of any person or organization listed on the platform'],
            ],
          },
          {
            type: 'p',
            inlines: ['Participation in any activity is at your own discretion and risk.'],
          },
        ],
      },
      {
        title: ['10. Assumption of Risk & No Professional Advice'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'Running, cycling, and outdoor activities can involve risks (weather, terrain, questionable hydration strategies, etc.). By using the Service, you acknowledge these risks and agree that ORO move is ',
              { t: 'strong', v: 'not liable' },
              ' for injuries, damages, or losses that occur during or because of any activity organized via the platform.',
            ],
          },
          {
            type: 'p',
            inlines: ['We provide digital tools. The physical world is still up to you.'],
          },
          {
            type: 'box',
            tone: 'surface',
            title: 'No Professional Advice:',
            blocks: [
              {
                type: 'p',
                inlines: [
                  'ORO move does not provide medical, training, safety, or professional advice. Any information shared through the Service, including event descriptions, club announcements, or user discussions, is for general coordination purposes only. You are responsible for evaluating your own fitness, safety, and participation decisions.',
                ],
              },
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['11. Service Modifications'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'We may update, change, or discontinue features of the Service at any time. We try to avoid surprises, but we reserve the right to ship improvements without sending up a flare first.',
            ],
          },
        ],
      },
      {
        title: ['12. Termination'],
        headingLevel: 'h3',
        blocks: [
          { type: 'p', inlines: ['We may suspend or terminate your access if:'] },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['You violate these Terms'],
              ['You misuse the platform'],
              ['You engage in harmful or illegal activities'],
            ],
          },
          {
            type: 'p',
            inlines: [
              'Objectionable content and abusive behavior are the exception to the "may" above. As our Acceptable Use section explains, we act on reports within 24 hours, and content we confirm is objectionable is removed and the user who posted it is ejected.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'You may stop using the Service at any time. Certain rights survive termination (e.g., licensing, disclaimers, limitations of liability).',
            ],
          },
        ],
      },
    ],
    [
      {
        title: ['13. Disclaimers'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'The Service is provided ',
              { t: 'strong', v: '"as is"' },
              ' and ',
              { t: 'strong', v: '"as available"' },
              ' with no warranties of any kind, express or implied.',
            ],
          },
          { type: 'p', inlines: ['We do not guarantee:'] },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['Uninterrupted service'],
              ['Bug-free code'],
              ['100% accuracy of content'],
              ['That your club will suddenly become wildly popular'],
            ],
          },
          { type: 'p', inlines: ['Use the Service at your own discretion.'] },
        ],
      },
      {
        title: ['14. Limitation of Liability'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'To the maximum extent permitted by law, ORO move shall not be liable for:',
            ],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['Indirect, incidental, special, consequential, or punitive damages'],
              ['Loss of data, opportunities, or goodwill'],
              ['Injuries or incidents during any event or activity'],
              ['Actions or omissions of clubs, organizers, or other users'],
            ],
          },
          {
            type: 'p',
            inlines: [
              "If we're legally responsible for anything, our total liability is limited to the amount you paid us in the last 12 months (which, for many users, is $0, and math is wild).",
            ],
          },
        ],
      },
      {
        title: ['15. Indemnification'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'You agree to indemnify and hold ORO move harmless from any claims, damages, liabilities, and expenses arising from:',
            ],
          },
          {
            type: 'list',
            style: 'bullet',
            items: [
              ['Your use of the Service'],
              ['Your User Content'],
              ['Your violation of these Terms'],
            ],
          },
          {
            type: 'p',
            inlines: ["Or, put simply: please don't make us hire extra lawyers."],
          },
        ],
      },
    ],
    [
      {
        title: ['16. Third-Party Services'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'The Service may integrate with third-party tools or platforms. Your use of those is governed by their respective terms. We are not responsible for their content or actions.',
            ],
          },
        ],
      },
      {
        title: ['17. Governing Law & International Use'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'These Terms are governed by the laws of the State of West Virginia, without regard to conflict-of-law principles.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'Any disputes will be resolved in state or federal courts located within West Virginia.',
            ],
          },
          {
            type: 'p',
            inlines: [
              'The Service is operated from the United States. If you access it from other locations, you are responsible for complying with local laws and regulations.',
            ],
          },
        ],
      },
      {
        title: ['18. Changes to These Terms'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              "We may update these Terms periodically. If we make material changes, we'll let you know in a reasonable way (in-app notice, email, or similar).",
            ],
          },
          {
            type: 'p',
            inlines: [
              'Your continued use of the Service after changes become effective means you accept the updated Terms.',
            ],
          },
        ],
      },
      {
        title: ['19. Contact'],
        headingLevel: 'h3',
        blocks: [
          {
            type: 'p',
            inlines: [
              'If you have questions, concerns, or find a clever typo we somehow missed, ',
              { t: 'link', v: 'get in touch', href: '/contact' },
              '.',
            ],
          },
        ],
      },
    ],
  ],
};
